import type {
  Device,
  DeviceView,
  DeviceListItem,
  Merchant,
  Protection,
  ProtectionState,
  ProtectionType,
  ProtectionView,
} from '../types'
import { daysUntil } from './dates'
import { requiresUserConfirmation } from './evidence'

export function toDeviceView(device: Device): DeviceView {
  const { image_key, ...view } = device
  return { ...view, has_image: image_key !== null }
}

/** Prag za blok „Istječe uskoro”. */
export const EXPIRING_SOON_DAYS = 90

export const PROTECTION_TYPE_LABELS: Record<ProtectionType, string> = {
  legal_conformity: 'Zakonska zaštita',
  manufacturer_warranty: 'Jamstvo proizvođača',
  merchant_extended_warranty: 'Dodatno jamstvo trgovca',
  insurance: 'Osiguranje',
  other: 'Ostala zaštita',
}

export const PROTECTION_STATE_LABELS: Record<ProtectionState, string> = {
  active: 'Aktivno',
  expiring_soon: 'Istječe uskoro',
  expired: 'Isteklo',
  needs_confirmation: 'Potrebna potvrda',
  unknown: 'Nije poznato',
}

export function protectionTypeLabel(type: ProtectionType): string {
  return PROTECTION_TYPE_LABELS[type] ?? PROTECTION_TYPE_LABELS.other
}

/**
 * Status pojedine zaštite.
 *
 * Redoslijed odlučivanja je namjeran:
 * 1. isteklo je isteklo, bez obzira na izvor
 * 2. nepotvrđen izvor (procijenjeno / nepoznato) traži korisničku potvrdu
 * 3. tek onda gledamo koliko je vremena ostalo
 */
export function computeProtectionState(protection: Protection, now: Date = new Date()): ProtectionState {
  const remaining = daysUntil(protection.expires_at, now)
  if (remaining !== null && remaining < 0) return 'expired'
  if (requiresUserConfirmation(protection.evidence_status)) return 'needs_confirmation'
  if (remaining === null) return 'unknown'
  if (remaining <= EXPIRING_SOON_DAYS) return 'expiring_soon'
  return 'active'
}

export function toProtectionView(protection: Protection, now: Date = new Date()): ProtectionView {
  return {
    ...protection,
    state: computeProtectionState(protection, now),
    days_remaining: daysUntil(protection.expires_at, now),
    needs_user_confirmation: requiresUserConfirmation(protection.evidence_status),
  }
}

const DEVICE_STATE_PRIORITY: ProtectionState[] = [
  'expiring_soon',
  'active',
  'needs_confirmation',
  'expired',
  'unknown',
]

/** Ukupni status uređaja — ono što korisnik vidi na kartici. */
export function computeDeviceState(protections: ProtectionView[]): ProtectionState {
  if (protections.length === 0) return 'unknown'
  for (const candidate of DEVICE_STATE_PRIORITY) {
    if (protections.some((p) => p.state === candidate)) return candidate
  }
  return 'unknown'
}

/**
 * Najvažniji datum isteka: najbliži budući istek među zaštitama koje još traju.
 * Ako ništa ne traje, uzimamo posljednji istekli datum (za prikaz „Isteklo”).
 *
 * Potvrđene zaštite imaju prednost pred procijenjenima čak i kad procijenjena
 * istječe ranije — datum koji nosi zeleni status ne smije doći iz procjene.
 */
export function primaryExpiry(protections: ProtectionView[]): ProtectionView | null {
  const live = protections
    .filter((p) => p.expires_at !== null && (p.days_remaining ?? -1) >= 0)
    .sort((a, b) => (a.days_remaining ?? 0) - (b.days_remaining ?? 0))
  const confirmed = live.filter((p) => !p.needs_user_confirmation)
  if (confirmed[0]) return confirmed[0]
  if (live[0]) return live[0]
  const expired = protections
    .filter((p) => p.expires_at !== null)
    .sort((a, b) => (b.days_remaining ?? 0) - (a.days_remaining ?? 0))
  return expired[0] ?? null
}

const URGENCY_BASE: Record<ProtectionState, number> = {
  expiring_soon: 0,
  needs_confirmation: 1_000,
  active: 10_000,
  unknown: 50_000,
  expired: 100_000,
}

/** Manji broj = hitnije. Koristi se za sortiranje popisa uređaja. */
export function computeUrgency(state: ProtectionState, daysRemaining: number | null, openClaims = 0): number {
  if (openClaims > 0) return -1_000 + Math.min(openClaims, 999)
  const base = URGENCY_BASE[state]
  if (state === 'expiring_soon' || state === 'active') {
    return base + Math.max(0, daysRemaining ?? 9_999)
  }
  return base
}

export function buildDeviceListItem(params: {
  device: Device
  merchant: Merchant | null
  protections: Protection[]
  openClaims?: number
  now?: Date
}): DeviceListItem {
  const now = params.now ?? new Date()
  const views = params.protections.map((p) => toProtectionView(p, now))
  const primary = primaryExpiry(views)
  // Ako je jedini živi rok procijenjen, uređaj ne smije nositi zeleni ili narančasti
  // rok — traži potvrdu, bez obzira što bi vremenski izračun rekao.
  const computed = computeDeviceState(views)
  const state =
    (computed === 'active' || computed === 'expiring_soon') && primary?.needs_user_confirmation
      ? 'needs_confirmation'
      : computed
  const openClaims = params.openClaims ?? 0
  return {
    device: toDeviceView(params.device),
    merchant: params.merchant,
    protections: views,
    overall_state: state,
    primary_expiry: primary?.expires_at ?? null,
    primary_days_remaining: primary?.days_remaining ?? null,
    urgency: computeUrgency(state, primary?.days_remaining ?? null, openClaims),
    open_claims: openClaims,
  }
}

export function sortByUrgency(items: DeviceListItem[]): DeviceListItem[] {
  return [...items].sort((a, b) => {
    if (a.urgency !== b.urgency) return a.urgency - b.urgency
    return a.device.name.localeCompare(b.device.name, 'hr')
  })
}

export type DeviceFilter = 'all' | 'active' | 'expiring_soon' | 'expired' | 'needs_confirmation'

export function matchesFilter(item: DeviceListItem, filter: DeviceFilter): boolean {
  if (filter === 'all') return true
  if (filter === 'active') return item.overall_state === 'active' || item.overall_state === 'expiring_soon'
  if (filter === 'needs_confirmation') {
    return item.protections.some((p) => p.needs_user_confirmation)
  }
  return item.overall_state === filter
}

export function matchesSearch(item: DeviceListItem, rawQuery: string): boolean {
  const query = rawQuery.trim().toLowerCase()
  if (query.length === 0) return true
  const haystack = [
    item.device.name,
    item.device.brand,
    item.device.model,
    item.device.serial_number,
    item.merchant?.name ?? null,
  ]
    .filter((value): value is string => typeof value === 'string' && value.length > 0)
    .join(' ')
    .toLowerCase()
  return query
    .split(/\s+/)
    .every((token) => haystack.includes(token))
}
